import { componentGroupCls } from "@faire/slate/lib/tracking";
import { tsv } from "@faire/slate/lib/tsv";
import { OverflowLayout } from "@faire/slate/private/layout/OverflowLayout";
import {
  IWebComponentProps,
  OverflowVariant,
} from "@faire/slate/private/shared/types";
import { ISlateTagGroupPropConstraints } from "@faire/slate/private/tag/types";
import React from "react";

import { Tag } from "./Tag";

type ITagProps = React.ComponentProps<typeof Tag>;

type IProps = {
  /**
   * **web**
   * The props of all the tags in the tag group.
   */
  items: Array<Omit<ITagProps, "variant">>;

  /**
   * Controls the overflow behavior of the tag group.
   *
   * @default "scroll"
   */
  overflow?: OverflowVariant;

  /**
   * The variants of all the tags in the tag group.
   *
   * @default "neutral"
   */
  variant?: ITagProps["variant"];

  // DO NOT ADD any new prop without aligning with Slate team first
} & IWebComponentProps;

/**
 * A group of Tags.
 *
 * A tag contains a descriptive word or phrase associated with another object.
 *
 * See [migration guide](migration-guide.mdx) for more information.
 *
 * {@link https://www.notion.so/faire/Tag-2242efb5c25a80749f87e230fa5a0f89 | Slate/TagGroup}
 */
export const TagGroup: React.FC<ISlateTagGroupPropConstraints<IProps>> = ({
  items,
  overflow = "scroll",
  variant = "neutral",
  ...props
}) => (
  <OverflowLayout
    {...props}
    className={tagGroupStyles.cls({ overflow, className: componentGroupCls() })}
    data-component-name="Slate/TagGroup"
    overflow={overflow}
    role="list"
  >
    {items.map(({ label, onDismiss, ...tagProps }, i) => (
      <Tag
        key={`tag-${i}-${label}`}
        {...tagProps}
        label={label}
        role="listitem"
        {...(variant === "neutral" ? { variant, onDismiss } : { variant })}
      />
    ))}
  </OverflowLayout>
);

const tagGroupStyles = tsv({
  base: "fs-tag-group gap-2",
  variants: {
    overflow: {
      scroll: "[&_.fs-tag]:whitespace-nowrap",
      wrap: "[&_.fs-tag]:max-w-full",
    },
  },
});
